import api from '@/utils/api.utils';
import {
  Deadline,
  DeadlineCreate,
  DeadlineUpdate,
  DeadlineFilters,
  DeadlineListResponse,
} from '@/types/deadline';

const BASE_URL = '/api/v1/deadlines';

/**
 * Deadline Service
 * Handles all API calls related to deadlines
 */
export const deadlineService = {
  /**
   * Create a new deadline
   */
  createDeadline: async (data: DeadlineCreate): Promise<Deadline> => {
    const response = await api.post<Deadline>(BASE_URL, data);
    return response.data;
  },

  /**
   * Get all deadlines for the current user with optional filters
   */
  getDeadlines: async (filters?: DeadlineFilters): Promise<DeadlineListResponse> => {
    const params = new URLSearchParams();
    
    if (filters?.priority) {
      params.append('priority', filters.priority);
    }
    if (filters?.source_type) {
      params.append('source_type', filters.source_type);
    }
    if (filters?.completed !== undefined) {
      params.append('completed', String(filters.completed));
    }
    if (filters?.start_date) {
      params.append('start_date', filters.start_date);
    }
    if (filters?.end_date) {
      params.append('end_date', filters.end_date);
    }

    const url = params.toString() ? `${BASE_URL}?${params.toString()}` : BASE_URL;
    const response = await api.get<DeadlineListResponse>(url);
    return response.data;
  },

  /**
   * Get a specific deadline by ID
   */
  getDeadlineById: async (id: number): Promise<Deadline> => {
    const response = await api.get<Deadline>(`${BASE_URL}/${id}`);
    return response.data;
  },

  /**
   * Update an existing deadline
   */
  updateDeadline: async (id: number, data: DeadlineUpdate): Promise<Deadline> => {
    const response = await api.put<Deadline>(`${BASE_URL}/${id}`, data);
    return response.data;
  },

  /**
   * Delete a deadline
   */
  deleteDeadline: async (id: number): Promise<void> => {
    await api.delete(`${BASE_URL}/${id}`);
  },

  /**
   * Mark a deadline as complete or incomplete
   */
  markDeadlineComplete: async (id: number, completed: boolean = true): Promise<Deadline> => {
    const response = await api.patch<Deadline>(
      `${BASE_URL}/${id}/complete?completed=${completed}`
    );
    return response.data;
  },
};

export default deadlineService;
